import yaml from 'js-yaml';
import { ThemeRegistry } from '../components/ThemeProvider';

// Use js-yaml for proper YAML parsing
function parseThemeYaml(yamlContent: string): ThemeRegistry {
  try {
    const parsed = yaml.load(yamlContent) as Record<
      string,
      Record<string, string>
    >;

    // Convert to our ThemeRegistry format
    const registry: ThemeRegistry = {};

    for (const [themeName, themeConfig] of Object.entries(parsed)) {
      if (typeof themeConfig === 'object' && themeConfig !== null) {
        registry[themeName] = themeConfig;
      }
    }

    return registry;
  } catch (error) {
    console.error('Failed to parse YAML theme config:', error);
    throw error;
  }
}

// Check if we're in an Electron environment
const isElectron = () => {
  return (
    typeof window !== 'undefined' &&
    (window as any).process &&
    (window as any).process.type
  );
};

// Check if we're in a web environment
const isWeb = () => {
  return typeof window !== 'undefined' && !isElectron();
};

/**
 * Load theme configuration at runtime
 * Works for both Electron (desktop) and web environments
 */
export async function loadRuntimeTheme(
  themeConfigPath: string,
  appName: string = 'default',
): Promise<ThemeRegistry> {
  try {
    if (isElectron()) {
      // Electron environment - use IPC to read from main process
      return await loadThemeFromElectron(themeConfigPath);
    } else if (isWeb()) {
      // Web environment - use fetch to load as static asset
      return await loadThemeFromWeb(themeConfigPath);
    } else {
      // Fallback - return empty registry
      console.warn('Unknown environment, returning empty theme registry');
      return {};
    }
  } catch (error) {
    console.error(`Failed to load theme for ${appName}:`, error);
    throw error;
  }
}

/**
 * Load theme from Electron main process via IPC
 */
async function loadThemeFromElectron(
  themeConfigPath: string,
): Promise<ThemeRegistry> {
  // Check if we have access to Electron IPC
  if (typeof window !== 'undefined' && (window as any).electronAPI) {
    try {
      console.log(
        '[RuntimeThemeLoader] Reading theme file via IPC:',
        themeConfigPath,
      );
      const yamlContent = await (window as any).electronAPI.readFile(
        themeConfigPath,
      );
      console.log(
        '[RuntimeThemeLoader] YAML content received, length:',
        yamlContent?.length,
      );
      return parseThemeYaml(yamlContent);
    } catch (error) {
      console.error(
        '[RuntimeThemeLoader] Failed to read theme file via Electron IPC:',
        error,
      );
      throw error;
    }
  } else {
    console.error('[RuntimeThemeLoader] Electron API not available');
    throw new Error('Electron API not available, cannot load theme');
  }
}

/**
 * Load theme from web environment using fetch
 */
async function loadThemeFromWeb(
  themeConfigPath: string,
): Promise<ThemeRegistry> {
  try {
    // Convert file path to URL (assuming the YAML file is in public directory)
    const url = themeConfigPath.startsWith('/')
      ? themeConfigPath
      : `/${themeConfigPath}`;

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(
        `Failed to fetch theme file: ${response.status} ${response.statusText}`,
      );
    }

    const yamlContent = await response.text();

    return parseThemeYaml(yamlContent);
  } catch (error) {
    console.error('Failed to fetch theme file:', error);
    throw error;
  }
}
